// client/src/components/FormControls.tsx
import React, { useId, useRef, useEffect } from "react";
import attachIcon from '../../assets/Attach.png';

export type FieldProps = React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> & {
  label: string;
  error?: string;
  textarea?: boolean;
  rows?: number;
  required?: boolean;
  classPrefix?: string; // Optional prefix for scoped styling (e.g., 'di-' for data inquiry)
  labelClassName?: string;
};

export function Field({ label, error, textarea, rows = 3, required = true, classPrefix = '', labelClassName, ...rest }: FieldProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (textarea && textareaRef.current) {
      const el = textareaRef.current;
      const minHeightPx = 52;
      el.style.minHeight = `${minHeightPx}px`;
      const currentValue = typeof rest.value === 'string' ? rest.value : el.value;

      if (!currentValue) {
        el.style.height = `${minHeightPx}px`;
        return;
      }

      el.style.height = '0px';
      const nextHeight = Math.max(el.scrollHeight, minHeightPx);
      el.style.height = `${nextHeight}px`;
    }
  }, [textarea, rest.value]);

  return (
    <div className={`${classPrefix}input-float-wrap`}>
      <label className={labelClassName || `${classPrefix}input-float-label`} htmlFor={rest.id}>
        {label}{required && <span className="req">*</span>}
      </label>
      {textarea ? (
        <textarea
          {...rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>}
          className={`${classPrefix}textarea ${classPrefix}input-float ${error ? "input-error" : ""}`}
          rows={rows}
          ref={textareaRef}
          style={{ overflow: 'hidden', resize: 'none' }}
        />
      ) : (
        <input {...rest} className={`${classPrefix}input ${classPrefix}input-float ${error ? "input-error" : ""}`} />
      )}
      {error && <div className="error-text">{error}</div>}
    </div>
  );
}

export function Check({
  id, name, checked, onChange, error, children,
}: {
  id: string; name: string; checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string; children: React.ReactNode;
}) {
  return (
    <div className="consent">
      <input id={id} name={name} type="checkbox" className="checkbox" checked={checked} onChange={onChange} />
      <label htmlFor={id} className="consent-text">
        {children}
        {error && <div className="error-text">{error}</div>}
      </label>
    </div>
  );
}

export function FilePicker({ label, files, onChange, multiple, error, title }: {
  label: string;
  files: File[];
  multiple?: boolean;
  error?: string;
  title?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputId = useId();
  const [showTooltip, setShowTooltip] = React.useState(false);

  return (
    <div className="files-row">
      <label
        htmlFor={inputId}
        className="file-upload-label"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        style={{ position: "relative" }}
      >
        <span>{label}</span>
        {title && showTooltip && (
          <div className="tooltip">
            {title}
          </div>
        )}
      </label>

      <input
        id={inputId}
        ref={inputRef}
        type="file"
        style={{ display: "none" }}
        onChange={onChange}
        multiple={!!multiple}
      />

      {!!files.length && (
        <ul className="selected-files-list">
          {files.map((f) => (
            <li key={f.name}>{f.name}</li>
          ))}
        </ul>
      )}

      {error && <div className="error-text">{error}</div>}
    </div>
  );
}
